import React from 'react';
import { useLocale } from '@app/core/hooks/useLocale';
import { OpmFirstLastWaterfallDictionaryDto } from '@app/shared/models/contracts/opm-calculation-results-dto';
import { TrancheLevelRow } from './row-components/TrancheLevelRow';
import InstrumentLevelRow from './row-components/InstrumentLevelRow';

interface InstrumentExitDistributionsTotalsProps {
  instrumentHasTranches: boolean;
  instrumentName: string;
  interpolationOption: { escapeValue: boolean };
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto;
  selectedYear: string;
  firstSimulation: string;
  waterfallIterations: number;
  instrumentKey: string;
  currentTrancheIndex: number;
  instrumentHasDloc?: boolean;
}

const InstrumentExitDistributionsTotals: React.FC<InstrumentExitDistributionsTotalsProps> = ({
  instrumentHasTranches,
  instrumentName,
  interpolationOption,
  opmWaterfall,
  selectedYear,
  firstSimulation,
  waterfallIterations,
  instrumentKey,
  currentTrancheIndex,
  instrumentHasDloc,
}) => {
  const { l } = useLocale();
  const netProceedsToInstrumentName = instrumentHasTranches
    ? l('_NetProceedsToInstrumentTrancheN', {
        instrument: instrumentName,
        n: currentTrancheIndex + 1,
        interpolation: interpolationOption,
      })
    : l('_NetProceedsToInstrumentName', {
        instrument: instrumentName,
        interpolation: interpolationOption,
      });

  const fairValueName = instrumentHasTranches
    ? l('_PvOfInstrumentTrancheN', {
        instrument: instrumentName,
        n: currentTrancheIndex + 1,
        interpolation: interpolationOption,
      })
    : l('_PVOf', {
        Instrument: instrumentName,
        interpolation: interpolationOption,
      });

  const discountFairValueName = instrumentHasTranches
    ? l('_MvOfInstrumentTrancheN', {
        instrument: instrumentName,
        n: currentTrancheIndex + 1,
        interpolation: interpolationOption,
      })
    : l('_MVOf', {
        Instrument: instrumentName,
        interpolation: interpolationOption,
      });

  return (
    <>
      <TrancheLevelRow
        label="_DistributionsPaid"
        valueKey="trancheDistribution"
        opmWaterfall={opmWaterfall}
        selectedYear={selectedYear}
        firstSimulation={firstSimulation}
        waterfallIterations={waterfallIterations}
        instrumentKey={instrumentKey}
        currentTrancheIndex={currentTrancheIndex}
      />
      <TrancheLevelRow
        label="_PostIPOLockInDiscount"
        valueKey="postIpoDlomPc"
        opmWaterfall={opmWaterfall}
        selectedYear={selectedYear}
        firstSimulation={firstSimulation}
        waterfallIterations={waterfallIterations}
        instrumentKey={instrumentKey}
        isPercentage
        currentTrancheIndex={currentTrancheIndex}
      />
      <TrancheLevelRow
        label={netProceedsToInstrumentName}
        valueKey="netDistribution"
        opmWaterfall={opmWaterfall}
        selectedYear={selectedYear}
        firstSimulation={firstSimulation}
        waterfallIterations={waterfallIterations}
        instrumentKey={instrumentKey}
        isBold
        currentTrancheIndex={currentTrancheIndex}
      />
      <TrancheLevelRow
        label="_RiskFreeRate"
        valueKey="riskFreeRate"
        opmWaterfall={opmWaterfall}
        selectedYear={selectedYear}
        firstSimulation={firstSimulation}
        waterfallIterations={waterfallIterations}
        instrumentKey={instrumentKey}
        isPercentage
        currentTrancheIndex={currentTrancheIndex}
      />
      <InstrumentLevelRow
        label="_DiscountFactor"
        valueKey="discountFactor"
        opmWaterfall={opmWaterfall}
        selectedYear={selectedYear}
        waterfallIterations={waterfallIterations}
        instrumentKey={instrumentKey}
        decimalPoint={2}
      />
      <TrancheLevelRow
        label={fairValueName}
        valueKey="fairValue"
        opmWaterfall={opmWaterfall}
        selectedYear={selectedYear}
        firstSimulation={firstSimulation}
        waterfallIterations={waterfallIterations}
        instrumentKey={instrumentKey}
        isBold
        currentTrancheIndex={currentTrancheIndex}
      />
      {instrumentHasDloc && (
        <TrancheLevelRow
          label="_DLOC"
          valueKey="dlocPc"
          opmWaterfall={opmWaterfall}
          selectedYear={selectedYear}
          firstSimulation={firstSimulation}
          waterfallIterations={waterfallIterations}
          instrumentKey={instrumentKey}
          isPercentage
          currentTrancheIndex={currentTrancheIndex}
        />
      )}
      <TrancheLevelRow
        label="_DLOM"
        valueKey="dlomPc"
        opmWaterfall={opmWaterfall}
        selectedYear={selectedYear}
        firstSimulation={firstSimulation}
        waterfallIterations={waterfallIterations}
        instrumentKey={instrumentKey}
        isPercentage
        currentTrancheIndex={currentTrancheIndex}
      />
      <TrancheLevelRow
        label={discountFairValueName}
        valueKey="discountedFairValue"
        opmWaterfall={opmWaterfall}
        selectedYear={selectedYear}
        firstSimulation={firstSimulation}
        waterfallIterations={waterfallIterations}
        instrumentKey={instrumentKey}
        isBold
        currentTrancheIndex={currentTrancheIndex}
      />
    </>
  );
};

export default InstrumentExitDistributionsTotals;
